import { store } from '../../state/store';
import { LatestAttachmentContext } from '../../utils/types';
import { ChatOrchestratorBase, type Constructor } from './orchestrator-base';

/**
 * Infers missing tool-call input fields from the latest file attachment or the
 * latest tool result, by fuzzy-matching field names/labels against known context.
 * Self-contained — reads only from the global `store`, no shared orchestrator state.
 */
export function withContextEnrichment<TBase extends Constructor<ChatOrchestratorBase>>(Base: TBase) {
  return class extends Base {
    protected getLatestAttachmentContext(): LatestAttachmentContext | null {
      const messages = store.getState().messages;

      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.role !== 'user') continue;

        const attachment = msg.attachments?.[msg.attachments.length - 1];
        if (attachment) {
          return {
            filename: attachment.filename,
            storagePath: attachment.storagePath,
            mimeType: attachment.mimeType,
            originalName: attachment.originalName,
            sizeBytes: attachment.size,
            fileId: attachment.fileId,
            fileHandle: attachment.fileHandle,
          };
        }

        const content = typeof msg.content === 'string' ? msg.content : '';
        const matches = [...content.matchAll(/\[File:\s*([^\]]+?)\]/g)];
        const rawRef = matches[matches.length - 1]?.[1]?.trim();
        if (!rawRef) continue;

        return this.parseFileReference(rawRef);
      }

      return null;
    }

    protected getLatestToolResultObject(): Record<string, unknown> | null {
      const toolCalls = store.getState().toolCalls;

      for (let i = toolCalls.length - 1; i >= 0; i--) {
        const raw = toolCalls[i].result;
        if (!raw) continue;

        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && typeof parsed[0]?.text === 'string') {
            const nested = JSON.parse(parsed[0].text);
            if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
              return nested as Record<string, unknown>;
            }
          }
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed as Record<string, unknown>;
          }
        } catch {
          // ignore non-JSON results
        }
      }

      return null;
    }

    protected inferFieldValue(
      fieldName: string,
      prop: Record<string, unknown> | undefined,
      latestAttachment: LatestAttachmentContext | null,
      latestToolResult: Record<string, unknown> | null,
    ): unknown {
      const hints = [
        fieldName,
        typeof prop?.title === 'string' ? prop.title : '',
        typeof prop?.description === 'string' ? prop.description : '',
        typeof prop?.title_he === 'string' ? prop.title_he : '',
        typeof prop?.description_he === 'string' ? prop.description_he : '',
      ];
      const haystack = this.normalizeMatchText(hints.join(' '));
      const propType = typeof prop?.type === 'string' ? prop.type.toLowerCase() : '';
      const fileExtension = this.getFileExtension(latestAttachment?.filename || latestAttachment?.originalName);

      if (
        latestAttachment?.storagePath &&
        (
          haystack.includes('s3') ||
          haystack.includes('storagepath') ||
          haystack.includes('storage path') ||
          haystack.includes('s3 key') ||
          haystack.includes('object key') ||
          haystack.includes('file key') ||
          haystack.includes('filename from s3') ||
          haystack.includes('שם הקובץ ב s3')
        )
      ) {
        return latestAttachment.storagePath;
      }

      if (
        latestAttachment?.storagePath &&
        propType === 'array' &&
        (
          haystack.includes('documents') ||
          haystack.includes('attachments') ||
          haystack.includes('files') ||
          haystack.includes('מסמכים') ||
          haystack.includes('קבצים')
        )
      ) {
        return [latestAttachment.storagePath];
      }

      if (
        latestAttachment?.filename &&
        (
          haystack.includes('filename') ||
          haystack.includes('file name') ||
          haystack.includes('document name') ||
          haystack.includes('שם הקובץ')
        )
      ) {
        return latestAttachment.filename;
      }

      if (
        latestAttachment?.originalName &&
        (
          haystack.includes('original name') ||
          haystack.includes('original filename') ||
          haystack.includes('source filename') ||
          haystack.includes('שם מקורי')
        )
      ) {
        return latestAttachment.originalName;
      }

      if (
        latestAttachment?.mimeType &&
        (
          haystack.includes('mime') ||
          haystack.includes('mime type') ||
          haystack.includes('content type') ||
          haystack.includes('file type') ||
          haystack.includes('document type') ||
          haystack.includes('סוג קובץ') ||
          haystack.includes('סוג מסמך')
        )
      ) {
        return latestAttachment.mimeType;
      }

      if (
        fileExtension &&
        (
          haystack.includes('extension') ||
          haystack.includes('file extension') ||
          haystack.includes('suffix') ||
          haystack.includes('סיומת')
        )
      ) {
        return fileExtension;
      }

      if (
        typeof latestAttachment?.sizeBytes === 'number' &&
        (
          haystack.includes('size') ||
          haystack.includes('bytes') ||
          haystack.includes('file size') ||
          haystack.includes('גודל')
        )
      ) {
        return latestAttachment.sizeBytes;
      }

      if (
        latestAttachment?.fileId &&
        (
          haystack.includes('file id') ||
          haystack.includes('fileid') ||
          haystack.includes('attachment id') ||
          haystack.includes('מזהה קובץ')
        )
      ) {
        return latestAttachment.fileId;
      }

      if (
        latestAttachment?.fileHandle &&
        (
          haystack.includes('file handle') ||
          haystack.includes('filehandle') ||
          haystack.includes('handle') ||
          haystack.includes('ידית קובץ')
        )
      ) {
        return latestAttachment.fileHandle;
      }

      return this.findValueInObject(fieldName, hints, latestToolResult);
    }

    private findValueInObject(fieldName: string, hints: string[], obj: Record<string, unknown> | null): unknown {
      if (!obj) return undefined;

      const flattened = this.flattenObjectEntries(obj);
      const candidates = this.buildCandidateKeys(fieldName, hints);

      for (const entry of flattened) {
        const entryKey = this.normalizeMatchText(`${entry.path} ${entry.key}`);
        if (candidates.some(candidate => candidate && (entryKey === candidate || entryKey.endsWith(` ${candidate}`) || entryKey.includes(candidate)))) {
          return entry.value;
        }
      }

      return undefined;
    }

    private buildCandidateKeys(fieldName: string, hints: string[]): string[] {
      const candidates = new Set<string>();
      const base = this.normalizeMatchText(fieldName);
      if (base) {
        candidates.add(base);
        candidates.add(base.replace(/\s+/g, ''));
      }

      for (const hint of hints) {
        const normalized = this.normalizeMatchText(hint);
        if (!normalized) continue;
        candidates.add(normalized);

        const tokens = normalized.split(' ').filter(token => token.length >= 3);
        for (let i = 0; i < tokens.length; i++) {
          const single = tokens[i];
          candidates.add(single);
          if (i < tokens.length - 1) {
            candidates.add(`${tokens[i]} ${tokens[i + 1]}`);
          }
        }
      }

      return [...candidates];
    }

    private flattenObjectEntries(
      obj: Record<string, unknown>,
      parentPath = '',
    ): Array<{ key: string; path: string; value: unknown }> {
      const out: Array<{ key: string; path: string; value: unknown }> = [];

      for (const [key, value] of Object.entries(obj)) {
        const path = parentPath ? `${parentPath}.${key}` : key;
        out.push({ key, path, value });

        if (value && typeof value === 'object' && !Array.isArray(value)) {
          out.push(...this.flattenObjectEntries(value as Record<string, unknown>, path));
        }
      }

      return out;
    }

    private normalizeMatchText(text: string): string {
      return text
        .toLowerCase()
        .replace(/[_./-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    private parseFileReference(rawRef: string): LatestAttachmentContext {
      const parenIndex = rawRef.indexOf(' (');
      if (parenIndex === -1 || !rawRef.endsWith(')')) {
        return { filename: rawRef.trim() };
      }

      const filename = rawRef.slice(0, parenIndex).trim();
      const metaText = rawRef.slice(parenIndex + 2, -1);
      const meta = new Map<string, string>();

      for (const segment of metaText.split(';')) {
        const sep = segment.indexOf(':');
        if (sep === -1) continue;
        const key = segment.slice(0, sep).trim().toLowerCase();
        const value = segment.slice(sep + 1).trim();
        if (key && value) meta.set(key, value);
      }

      const sizeRaw = meta.get('size_bytes');
      const sizeBytes = sizeRaw ? Number(sizeRaw) : undefined;

      return {
        filename,
        storagePath: meta.get('s3_key'),
        mimeType: meta.get('mime_type'),
        originalName: meta.get('original_name'),
        sizeBytes: Number.isFinite(sizeBytes) ? sizeBytes : undefined,
        fileId: meta.get('file_id'),
        fileHandle: meta.get('file_handle'),
      };
    }

    private getFileExtension(filename?: string): string | undefined {
      if (!filename) return undefined;
      const idx = filename.lastIndexOf('.');
      if (idx === -1 || idx === filename.length - 1) return undefined;
      return filename.slice(idx + 1).toLowerCase();
    }
  };
}
